import React, {useEffect, useMemo, useState} from 'react';
import {Divider} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {t} from '../../../../../../services/i18n';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import CustomCollapse from '../../../../../common/CustomCollapse';
import TrackCollapseLabel from './TrackCollapseLabel';
import TrackCollapseBody from './TrackCollapseBody';
import {fetchApi} from '../../../../../../services/HTTP';
import {sortString} from '../../../../../../services/Helpers';
import ChooseHugToSwapSkeleton from '../Steps/ChooseHugToSwapSkeleton';
import {MembershipTypes} from '../../../../../../Configs/DatabaseConsts';
import FiltersHandler from '../../../../../common/FiltersHandler';
import {ALL_LOCATIONS_KEY, getTrackFiltersConfig} from './trackFiltersConfig';
import {updateTempSelection} from '../../../../../../redux/stepperSlice';

const ChooseTrack = ({setFieldValue, target, values}) => {
    const dispatch = useDispatch();
    const locationsBox = useSelector((state) => state.box.box?.locations_box);
    const selectedLocation = useSelector((state) => state.userSettings.scheduleLocation);
    const isMultiLocation = useMemo(() => (locationsBox?.length ?? 0) > 1, [locationsBox]);
    const userLocation = locationsBox?.find(location => location.id === target?.user.locationBox?.id) ?? selectedLocation;
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTrackId, setSelectedTrackId] = useState(() =>
        values?.track?.id != null ? String(values.track.id) : null
    );
    const [expandedTrackId, setExpandedTrackId] = useState(null);
    const [tracksList, setTracksList] = useState([]);
    const [filteredTracks, setFilteredTracks] = useState([]);
    const [showLocationGroups, setShowLocationGroups] = useState(isMultiLocation);

    const userId = target?.user?.id ?? target?.user?.user_fk;
    
    useEffect(() => {
        fetchTracksForRegistration();
    }, []);

    useEffect(() => {
        if (isLoading) {
            dispatch(updateTempSelection({canProceed: false}));
            return;
        }
        dispatch(updateTempSelection({canProceed: !!values?.track?.id}));
    }, [values?.track?.id, isLoading, dispatch]);

    useEffect(() => {
        return () => dispatch(updateTempSelection(null));
    }, [dispatch]);

    const fetchTracksForRegistration = async () => {
        try {
            setIsLoading(true);
            const res = await fetchApi(
                'seriesTrack/getTracksForRegistration',
                'POST',
                {userId},
                false,
                true
            );
            setTracksList(res ?? []);
        } catch (e) {
            console.error(e);
            setTracksList([]);
        } finally {
            setIsLoading(false);
        }
    };

    const activeTracks = useMemo(() =>
        tracksList.filter(track => track.active !== false && track.active !== 0),
    [tracksList]);

    const locationOptions = useMemo(() => {
        if (!isMultiLocation) return [];
        return [
            {value: ALL_LOCATIONS_KEY, label: t('all-locations')},
            ..._.sortBy(locationsBox ?? [], location => sortString(location.location)).map(location => ({
                value: location.id,
                label: location.location,
            })),
        ];
    }, [isMultiLocation, locationsBox]);

    const filtersConfig = useMemo(
        () => getTrackFiltersConfig({isMultiLocation, locationOptions, userLocation}),
        [isMultiLocation, locationOptions, userLocation],
    );

    const groupedTracks = useMemo(() => {
        const sortedTracks = _.sortBy(filteredTracks, track => sortString(track.name));
        if (!showLocationGroups) {
            return sortedTracks.length ? {[userLocation?.location ?? '']: sortedTracks} : {};
        }
        const grouped = _.groupBy(sortedTracks, track => track.locations_box?.location ?? '');
        const sortedKeys = _.sortBy(Object.keys(grouped), locationName => sortString(locationName));
        return _.fromPairs(sortedKeys.map(locationName => [locationName, grouped[locationName]]));
    }, [filteredTracks, showLocationGroups, userLocation?.location]);

    const isTrackDisabled = (track) => {
        return track.isBookedUser;
    };

    useEffect(() => {
        if (selectedTrackId !== null || values?.track?.id) {
            return;
        }
        const allTracks = Object.values(groupedTracks).flat();
        const firstAvailable = allTracks.find(track => !isTrackDisabled(track));
        if (firstAvailable) {
            const firstId = String(firstAvailable.id);
            setSelectedTrackId(firstId);
            setFieldValue('track', firstAvailable);
        }
    }, [groupedTracks, selectedTrackId, values?.track?.id]);

    const onExpand = (trackId) => setExpandedTrackId(prev => prev === trackId ? null : trackId);

    const onSelect = (trackId) => {
        if (selectedTrackId === trackId) {
            return;
        }
        const track = tracksList.find(item => item.id === Number(trackId));
        setSelectedTrackId(trackId);
        setFieldValue('track', track ?? null);
        setFieldValue('courses', null);
    };

    const onFiltered = (filtered, filterValues) => {
        setFilteredTracks(filtered);
        setShowLocationGroups(!filterValues.location || filterValues.location === ALL_LOCATIONS_KEY);
    };

    const buildTrackCollapseItem = (track) => ({
        key: String(track.id),
        label: <TrackCollapseLabel track={track} />,
        children: <TrackCollapseBody track={track} />,
        ribbon: getTrackRibbon(track),
    });

    const getTrackRibbon = (track) => {
        if (track.isBookedUser) return { text: t('already-booked'), color: '#E0E0E0', isDisabled: true };
    };

    if (isLoading) return <ChooseHugToSwapSkeleton />;

    return (
        <VStack space={'0.75rem'}>
            <FiltersHandler
                list={activeTracks}
                config={filtersConfig}
                onFiltered={onFiltered}
            />
            <VStack space={'1.25rem'}>
                <Divider style={{margin: 0}} />
                {Object.entries(groupedTracks).map(([locationName, tracksInLocation]) => (
                    <VStack key={locationName || 'single-location'} space={'1.25rem'}>
                        {showLocationGroups && locationName && (
                            <AppText fontSize={GlobalStyleAttributes.fonts.heading2Desktop} boldFont>
                                {locationName}
                            </AppText>
                        )}
                        <VStack space={'0'}>
                            <Divider style={{margin: 0}} />
                            {tracksInLocation.map((track, idx) => (
                                <React.Fragment key={track.id}>
                                    {idx > 0 && <Divider style={{margin: 0}} />}
                                    <CustomCollapse
                                        item={buildTrackCollapseItem(track)}
                                        isExpanded={expandedTrackId === String(track.id)}
                                        isSelected={selectedTrackId === String(track.id)}
                                        onExpand={onExpand}
                                        onSelect={onSelect}
                                        isBookedUser={isTrackDisabled(track)}
                                        radioOffsetWithRibbon={{ right: '1032px', left: '1040px' }}
                                    />
                                </React.Fragment>
                            ))}
                            <Divider style={{margin: 0}} />
                        </VStack>
                    </VStack>
                ))}
                {Object.keys(groupedTracks).length === 0 && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '2rem 0'}}>
                        {t('no-tracks-found')}
                    </AppText>
                )}
            </VStack>
        </VStack>
    );
};

export default ChooseTrack;
